
import React, { useRef } from "react";
import { Box, Typography, makeStyles } from "@material-ui/core";
import AppDialog from "components/Dialog/AppDialog";
import AppButton from "components/CustomButtons/AppButton";
import CustomDatePicker from "components/Input/CustomDatePicker";
import SelectField from "components/Select/SelectField";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { showToast } from "store/slices/toast";
import { Formik, Form, Field } from "formik";
import AppColor from "utilities/AppColor";
import AppInput from "components/Input/AppInput";
import { ACTION_OWNER, ACTION_STATUS } from "config/AppConfig";

const useStyles = makeStyles(() => ({
  sectionTitle: {
    fontSize: 13,
    fontWeight: 800,
    color: "#0D9488",
    textTransform: "uppercase",
    letterSpacing: "0.05em",
    margin: "10px 0 15px",
    display: "block",
  },
  control: {
    display: "flex",
    flexWrap: "wrap",
    flexDirection: "row",
    justifyContent: "flex-end",
    marginTop: 20,
    marginBottom: 10,
    gap: 8,
  },
  fileBox: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    padding: "10px 14px",
    background: "#F9FAFB",
    border: "1px solid #E5E7EB",
    borderRadius: 6,
    marginBottom: "1rem",
  },
  fileName: {
    fontSize: 13,
    color: "#374151",
    flex: 1,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
}));

const IncidentActionForm = ({
  open,
  initialAction,
  initialDueDate,
  initialOwner,
  initialAssignedToUserId,
  initialFile,
  initialFileName,
  initialStatus,
  isEdit,
  companyUserOptions,
  workerOptions,
  onClose,
  onConfirm,
}) => {
  const { t } = useTranslation();
  const classes = useStyles();
  const dispatch = useDispatch();
  const hiddenFileInputRef = useRef(null);

  const [localFile, setLocalFile] = React.useState(null);
  const [submitting, setSubmitting] = React.useState(false);

  React.useEffect(() => {
    setLocalFile(initialFile);
  }, [initialFile, open]);

  React.useEffect(() => {
    if (open) {
      setSubmitting(false);
    }
  }, [open]);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/jpg'];
      if (!allowedTypes.includes(file.type)) {
        dispatch(showToast({ type: 'error', message: t('validation.invalidFileImage') || 'Only PDF or Image files are allowed' }));
        return;
      }
      setLocalFile(file);
    }
  };

  const handleConfirm = async (values) => {
    setSubmitting(true);
    try {
      await onConfirm(values.actionName, values.dueDate, values.owner, values.assignedToUserId, localFile, values.status);
    } finally {
      setSubmitting(false);
    }
  };

  const fileName = localFile ? localFile.name : (initialFileName || '');

  return (
    <AppDialog
      open={open}
      title={isEdit ? "Edit Action" : "Add Action"}
      onClose={() => {
        setLocalFile(null);
        onClose();
      }}
      maxWidth="sm"
    >
      <Box>
        <Formik
          enableReinitialize
          initialValues={{
            actionName: initialAction || '',
            dueDate: initialDueDate || '',
            owner: initialOwner || '',
            assignedToUserId: initialAssignedToUserId || '',
            status: initialStatus || '',
          }}
          validate={(values) => {
            const errors = {};
            if (!values.actionName) {
              errors.actionName = t('validation.required');
            }
            if (!values.dueDate) {
              errors.dueDate = t('validation.required');
            }
            if (!values.owner) {
              errors.owner = t('validation.required');
            }
            if (!values.assignedToUserId) {
              errors.assignedToUserId = t('validation.required');
            }
            return errors;
          }}
          onSubmit={handleConfirm}
        >
          {({ values, setFieldValue }) => {
            return (
              <Form>
                <Typography className={classes.sectionTitle}>
                  Upload File
                </Typography>
                <Box className={classes.fileBox}>
                  <i className="fas fa-file" style={{ fontSize: 18, color: "#6B7280" }} />
                  <Box className={classes.fileName}>
                    {fileName ? fileName : <span style={{ color: "red" }}>No file selected</span>}
                  </Box>
                  <AppButton
                    label={fileName ? "Change" : "Upload"}
                    mode="outlined"
                    rounder
                    style={{ fontSize: 11, textTransform: "none", height: 28 }}
                    onClick={() => {
                      if (hiddenFileInputRef.current) {
                        hiddenFileInputRef.current.value = null;
                        hiddenFileInputRef.current.click();
                      }
                    }}
                  />
                  <input
                    accept="image/jpeg,image/png,image/jpg,application/pdf"
                    style={{ display: 'none' }}
                    id="upload-modal-file-input"
                    type="file"
                    ref={hiddenFileInputRef}
                    onChange={handleFileChange}
                  />
                </Box>

                <Typography className={classes.sectionTitle}>
                  Action Details
                </Typography>
                <div className="row">
                  <div className="col-sm-12 col-md-12 mb-3">
                    <Field
                      component={AppInput}
                      name="actionName"
                      placeholder="Enter action name"
                    />
                  </div>

                  <div className="col-sm-12 col-md-6 mb-3">
                    <Field
                      component={SelectField}
                      name="owner"
                      label="Assign to"
                      required
                      options={[
                        { value: ACTION_OWNER.COMPANY, label: 'Company' },
                        { value: ACTION_OWNER.WORKER, label: 'Worker' },
                      ]}
                      onSelected={() => setFieldValue('assignedToUserId', '')}
                    />
                  </div>

                  <div className="col-sm-12 col-md-6 mb-3">
                    <Field
                      component={CustomDatePicker}
                      label="Due Date"
                      name="dueDate"
                      type="text"
                      formatDateJob={true}
                      required
                      classInput={'date-picker-container-custom'}
                    />
                  </div>

                  {(values.owner === ACTION_OWNER.COMPANY || values.owner === ACTION_OWNER.WORKER) && (
                    <div className="col-sm-12 col-md-6 mb-3">
                      <Field
                        component={SelectField}
                        name="assignedToUserId"
                        label="Select owner"
                        required
                        options={(values.owner === ACTION_OWNER.WORKER ? workerOptions : companyUserOptions) || []}
                      />
                    </div>
                  )}
                </div>

                {isEdit && (
                  <div className="row">
                    <div className="col-sm-12 col-md-6 mb-3">
                      <Field
                        component={SelectField}
                        name="status"
                        label={'Status'}
                        options={[
                          { value: ACTION_STATUS.OPEN, label: 'Open' },
                          { value: ACTION_STATUS.IN_PROGRESS, label: 'In Progress' },
                          { value: ACTION_STATUS.COMPLETED, label: 'Completed' },
                        ]}
                      />
                    </div>
                  </div>
                )}

                <Box className={classes.control}>
                  <AppButton
                    label="Cancel"
                    onClick={() => {
                      setLocalFile(null);
                      onClose();
                    }}
                    disabled={submitting}
                    buttonColor="#F3F4F6"
                    rounder
                    style={{ color: "#111827" }}
                  />
                  <AppButton
                    label="Confirm"
                    rounder
                    type="submit"
                    isLoading={submitting}
                    disabled={submitting}
                    buttonColor={AppColor.mainColor}
                  />
                </Box>
              </Form>
            );
          }}
        </Formik>
      </Box>
    </AppDialog>
  );
};

export default IncidentActionForm;
